"use strict";
/**
 * Additional Markets — button selection grid
 *
 * Renders the "Assign to Buttons" checkbox grid in the additional-markets
 * modal from the order-button registry, filtered to the currently visible
 * order-button layout. Off-layout buttons already carried by an assignment
 * being edited are appended under a divider so saving an edit never silently
 * drops them (assignments on hidden layouts stay dormant, never pruned).
 *
 * @module handlers/modals/additional-markets-button-grid
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.renderButtonGrid = renderButtonGrid;
const state_1 = require("../../state");
const ui_1 = require("../../ui");
const order_button_registry_1 = require("../../services/order-button-registry");
const GRID_ID = "am-buttons-grid";
/** Build one checkbox entry matching the legacy static-grid markup. */
function buildEntry(descriptor, checked, offLayout) {
    const label = document.createElement("label");
    label.className = `am-button-label am-button-${descriptor.group}`;
    // Single-side descriptors (traditional, basketball) are colored by side
    // (yes=green, no=purple); baseball's merged descriptors fire from both
    // sides and keep their offense/defense group coloring instead.
    if (descriptor.side)
        label.classList.add(`am-button-side-${descriptor.side}`);
    if (offLayout)
        label.classList.add("am-button-offlayout");
    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.className = "am-button-checkbox";
    checkbox.dataset.buttonId = descriptor.id;
    checkbox.checked = checked;
    const text = document.createElement("span");
    text.textContent = descriptor.label;
    label.appendChild(checkbox);
    label.appendChild(text);
    return label;
}
/**
 * Render the button-selection grid for the active order layout.
 *
 * @param checkedIds - Button IDs to render pre-checked.
 * @param extraIds   - Button IDs to render even when outside the active
 *                     layout (edit mode: the assignment's dormant buttons).
 *                     Rendered checked-if-in-checkedIds under an
 *                     "Other layouts" divider, visually dimmed.
 *
 * When the dynamic grid container is absent (legacy static-grid DOM used by
 * some tests), falls back to setting checked state on whatever
 * `.am-button-checkbox` elements exist.
 */
function renderButtonGrid(checkedIds, extraIds = []) {
    const grid = document.getElementById(GRID_ID);
    if (!grid) {
        document
            .querySelectorAll(".am-button-checkbox")
            .forEach((cb) => {
            cb.checked = checkedIds.has(cb.dataset.buttonId ?? "");
        });
        return;
    }
    const layout = (0, ui_1.resolveOrderLayout)((0, state_1.getState)().tradingSettings);
    const layoutButtons = (0, order_button_registry_1.getButtonsForLayout)(layout);
    grid.replaceChildren();
    for (const descriptor of layoutButtons) {
        grid.appendChild(buildEntry(descriptor, checkedIds.has(descriptor.id), false));
    }
    const inLayout = new Set(layoutButtons.map((b) => b.id));
    const offLayoutIds = extraIds.filter((id) => !inLayout.has(id));
    if (offLayoutIds.length === 0)
        return;
    const divider = document.createElement("div");
    divider.className = "am-offlayout-divider";
    divider.textContent = "Other layouts";
    grid.appendChild(divider);
    for (const id of offLayoutIds) {
        const descriptor = (0, order_button_registry_1.getButtonDescriptor)(id);
        if (!descriptor)
            continue;
        grid.appendChild(buildEntry(descriptor, checkedIds.has(id), true));
    }
}
